// Browser side of the Heavybit Identity front door: the lazily loaded Stytch
// B2B client, the Discovery OAuth start, the exchange call, and session
// adoption. Nothing here touches Apollo, Pinia or the user store: the
// /identity/* routes are brand-neutral pages that serve the whole Stytch
// project. identity/stytchSession.ts reads the same client for Auger's
// own sign-in.
//
// The SDK is imported dynamically so pages that never need it never load it. Only the
// headless B2B entry is needed: the consent page (/identity/authorize) drives
// idp.oauthAuthorizeStart / oauthAuthorizeSubmit itself (see consent.ts for
// why the prebuilt StytchB2BIdentityProvider component is not used).
import { parseStytchDomain } from "@auger/identity"
import { identity as config } from "@/common/config"
import { IDENTITY_COPY } from "@/identity/copy"
import type { OAuthProvider } from "@/identity/providers"

export type StytchB2BModule = typeof import("@stytch/vanilla-js/b2b/headless")
export type StytchB2BClient = ReturnType<StytchB2BModule["createStytchB2BClient"]>
export type { OAuthProvider } from "@/identity/providers"

export const RETURN_TO_KEY = "identity:return_to"
// Carries origin_assertion across the OAuth round trip the same way
// RETURN_TO_KEY carries return_to: the browser leaves for Google/GitHub and
// comes back on a fresh navigation, so a query parameter present only at
// /identity/login has to survive in storage rather than in memory.
export const ORIGIN_ASSERTION_KEY = "identity:origin_assertion"

export class IdentityNotConfiguredError extends Error {
  constructor() {
    super("Heavybit Identity is not configured (VITE_STYTCH_PUBLIC_TOKEN is unset).")
    this.name = "IdentityNotConfiguredError"
  }
}

// Thrown by exchangeDiscoveryToken for any non-2xx from auth-session. `code`
// is the function's machine-readable error, `reason` its refinement.
export class IdentityExchangeError extends Error {
  status: number
  code: string
  reason?: string
  constructor(status: number, code: string, reason?: string) {
    super(`auth-session ${status}: ${code}${reason ? ` (${reason})` : ""}`)
    this.name = "IdentityExchangeError"
    this.status = status
    this.code = code
    this.reason = reason
  }
}

let modulePromise: Promise<StytchB2BModule> | null = null
export function loadStytchModule(): Promise<StytchB2BModule> {
  modulePromise ??= import("@stytch/vanilla-js/b2b/headless")
  return modulePromise
}

// Options for createStytchB2BClient. `customBaseUrl` is the current option
// for routing every SDK call through the project's custom auth domain
// (@stytch/vanilla-js 6.2.0 StytchClientOptions; the older
// endpointOptions.apiDomain / testApiDomain are marked deprecated in favour
// of it). Verified live 2026-09-10: a token exchanged through
// https://id-test.heavybit.com carries iss "https://id-test.heavybit.com",
// while the same flow through test.stytch.com carries "stytch.com/<project>"
// and puts that bare issuer in the RFC 9207 `iss` callback parameter, which a
// strict MCP client that discovered the AS at the custom domain rejects.
export type IdentityClientOptions = { customBaseUrl?: string }

// Normalizing and validating the domain belongs to @auger/identity's
// parseStytchDomain, which common/config.ts calls at load, so a bad value fails the boot instead of the
// first SDK call. This is the shape adapter on top of it.
export function identityClientOptions(domain: string | undefined): IdentityClientOptions {
  const origin = parseStytchDomain(domain)
  return origin ? { customBaseUrl: origin } : {}
}

// The SDK ships as a dynamic import, so this promise can reject for a reason
// that will not recur: a chunk fetch that fails because the tab is stale after
// a deploy, or a network blip. Caching that rejection would make the failure
// permanent for the life of the tab, so a failed attempt is forgotten and the
// next call re-imports. (modulePromise is cleared too — it is the half that
// actually holds the failed import.)
let clientPromise: Promise<StytchB2BClient> | null = null
export function getIdentityClient(): Promise<StytchB2BClient> {
  if (!config.stytchPublicToken) return Promise.reject(new IdentityNotConfiguredError())
  clientPromise ??= loadStytchModule()
    .then((mod) =>
      mod.createStytchB2BClient(
        config.stytchPublicToken!,
        identityClientOptions(config.stytchDomain)
      )
    )
    .catch((e: unknown) => {
      resetIdentityClient()
      throw e
    })
  return clientPromise
}

/** Forget a cached client (and a failed SDK import) so the next call retries. */
export function resetIdentityClient(): void {
  clientPromise = null
  modulePromise = null
}

export function callbackUrl(): string {
  return `${window.location.origin}/identity/callback`
}

export function rememberReturnTo(path: string): void {
  try {
    sessionStorage.setItem(RETURN_TO_KEY, path)
  } catch {
    // storage unavailable: the callback falls back to the done page
  }
}

export function takeReturnTo(): string | null {
  try {
    const value = sessionStorage.getItem(RETURN_TO_KEY)
    sessionStorage.removeItem(RETURN_TO_KEY)
    return value
  } catch {
    return null
  }
}

/**
 * Remember the origin_assertion query parameter (if any) across the OAuth
 * round trip, exactly like rememberReturnTo above. Opaque here: this file
 * never inspects, validates, or interprets the value — only the exchange
 * (verifyOriginAssertion in functions/common/stytch.js) holds the secret to
 * do that.
 */
export function rememberOriginAssertion(value: string | undefined): void {
  try {
    if (value) sessionStorage.setItem(ORIGIN_ASSERTION_KEY, value)
    else sessionStorage.removeItem(ORIGIN_ASSERTION_KEY)
  } catch {
    // storage unavailable: the callback simply has nothing to post, which is
    // the same "absent" case as a login that never carried one at all.
  }
}

export function takeOriginAssertion(): string | null {
  try {
    const value = sessionStorage.getItem(ORIGIN_ASSERTION_KEY)
    sessionStorage.removeItem(ORIGIN_ASSERTION_KEY)
    return value
  } catch {
    return null
  }
}

/**
 * Always asked for, on every sign-in.
 *
 * `prompt=select_account` makes Google show its chooser instead of silently
 * reusing whichever session the browser happens to be holding. The value is
 * exact and load-bearing: Google ignores a `prompt` it does not recognise, so
 * a typo here reinstates the original bug in silence. Tests pin the string.
 *
 * Not an option, and deliberately not an opt-in affordance. An affordance is
 * only ever found by somebody who already knows they have two identities —
 * which is the one person who does not need it. The person this protects
 * clicks "Continue with Google", is signed in as whoever the browser
 * remembers, and lands on the waitlist never knowing a choice existed. The
 * cost is one extra click per person per week (the Stytch session is an
 * absolute 7 days), and Google renders the chooser for a single account too:
 * that account plus "Use another account". It is never a dead end.
 *
 * GitHub documents no `prompt` parameter, so in practice this is Google-only.
 * An unknown query parameter on GitHub's authorize URL is ignored rather than
 * rejected, so sending it costs nothing there.
 */
export const ACCOUNT_CHOOSER_PARAMS: Record<string, string> = { prompt: "select_account" }

// Send the browser to Stytch's Discovery OAuth start for the provider. The SDK
// navigates during the call; nothing after it runs.
//
// `provider_params` is the SDK's documented passthrough: the discovery start
// puts every entry on the authorize URL as `provider_<key>`, which Stytch
// forwards to the provider (@stytch/vanilla-js 6.2.0,
// B2BOAuthDiscoveryStartOptions — its own doc example is
// `{"prompt": "select_account"}`). Going through the SDK rather than
// hand-building the authorize URL is what keeps PKCE, state and the RFC 9207
// `iss` check intact: discovery.start mints and stores the pkce_code_verifier
// itself, and a reconstructed URL is how all three get dropped.
export async function startOAuth(
  provider: OAuthProvider,
  returnTo: string,
  originAssertion?: string
): Promise<void> {
  const client = await getIdentityClient()
  rememberReturnTo(returnTo)
  rememberOriginAssertion(originAssertion)
  await client.oauth[provider].discovery.start({
    discovery_redirect_url: callbackUrl(),
    provider_params: ACCOUNT_CHOOSER_PARAMS,
  })
}

/**
 * The email address of the signed-in member, read from the SDK's own local
 * state. No Apollo, no Postgres, no user store: the waitlist page shows this,
 * and the `network` Hasura role it belongs to cannot read an email address
 * at all (it holds `users`.id and `users`.active on its own row, nothing more).
 *
 * Null whenever the answer is not already known — Identity not configured for
 * this build, no session, or the member cache not yet hydrated. Callers render
 * without it rather than waiting, because a card that shows nothing until a
 * round trip finishes is the hang this page exists to replace.
 */
export async function currentMemberEmail(): Promise<string | null> {
  try {
    const client = await getIdentityClient()
    return client.self.getSync()?.email_address ?? null
  } catch (e) {
    // "Not configured" is an ordinary state (the dev/test auth bypass) and
    // says nothing. Anything else is a fact worth recording: a bare catch here
    // would hide an SDK shape change — `member.getSync()` is already deprecated
    // in favour of `self.getSync()`, so the next major is a live risk — and the
    // symptom would be the account line silently vanishing from every consent
    // screen in production, with a green test suite, because the specs mock
    // this function wholesale.
    if (!(e instanceof IdentityNotConfiguredError)) {
      console.error("Heavybit Identity: could not read the signed-in member", e)
    }
    return null
  }
}

export interface ExchangeResult {
  session_token: string
  session_jwt: string
  member_id: string
  organization_id: string
  class: "staff" | "network"
  /** The person's audience roles (x-hasura-allowed-roles), as the exchange
   *  minted them. The same list is in the session JWT; this copy saves the
   *  callback decoding it. */
  roles?: string[]
}

export interface ExchangeContext {
  /** Set only when the login started from a Connected App consent flow (see
   *  returnTo.ts). */
  agentClientId?: string
  /** The ORIGIN of the validated return_to, when that was an allowlisted
   *  external app. The exchange turns it into users.signup_source on a first
   *  login; the label itself is never sent from here, so a caller cannot
   *  choose what a signup is attributed to beyond the origins the allowlist
   *  already permits. */
  returnToOrigin?: string
  /** Crucible's opaque, HMAC-signed claim about that same origin (carried in
   *  from the origin_assertion query parameter — see originAssertionFrom in
   *  returnTo.ts). OPAQUE HERE: this file never inspects it, only forwards
   *  it; only the exchange holds the secret to verify it
   *  (verifyOriginAssertion in functions/common/stytch.js). A valid one
   *  proves the sign-in started at Crucible; an absent or malformed one
   *  proves nothing, and the exchange falls back to its unverified behaviour
   *  rather than failing the login. */
  originAssertion?: string
}

// The one-time discovery token goes to the exchange function; the session
// comes back.
export async function exchangeDiscoveryToken(
  token: string,
  context: ExchangeContext = {},
  fetchImpl: typeof fetch = fetch
): Promise<ExchangeResult> {
  const response = await fetchImpl("/.netlify/functions/auth-session", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XmlHttpRequest",
    },
    body: JSON.stringify({
      token,
      ...(context.agentClientId ? { agent_client_id: context.agentClientId } : {}),
      ...(context.returnToOrigin ? { return_to_origin: context.returnToOrigin } : {}),
      ...(context.originAssertion ? { origin_assertion: context.originAssertion } : {}),
    }),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new IdentityExchangeError(response.status, data?.error ?? "unknown_error", data?.reason)
  }
  return data as ExchangeResult
}

// updateSession stores the tokens; authenticate hydrates the SDK's member and
// session state from them (and confirms Stytch still honours the session).
export async function adoptSession(
  client: StytchB2BClient,
  tokens: Pick<ExchangeResult, "session_token" | "session_jwt">
): Promise<void> {
  client.session.updateSession({
    session_token: tokens.session_token,
    session_jwt: tokens.session_jwt,
  })
  await client.session.authenticate()
}

// Human copy for a refused or failed exchange. Brand-neutral on purpose.
export function describeExchangeError(error: unknown): string {
  if (error instanceof IdentityNotConfiguredError) {
    return "Heavybit Identity is not configured for this environment."
  }
  if (error instanceof IdentityExchangeError) {
    switch (error.reason ?? error.code) {
      // PLACEHOLDER COPY — pending Ted Carstensen. See identity/copy.ts.
      // The provider identity (usually a personal GitHub address) is not the
      // one this person's Heavybit account carries, and there is no account
      // linking yet. Never "access denied": nothing is wrong with them.
      case "identity_not_linked":
        return IDENTITY_COPY.identityNotLinked
      case "inactive_user":
        return "This account is deactivated. Contact Heavybit if you think this is a mistake."
      case "class_mismatch":
        return "This account cannot sign in this way. Contact Heavybit for help."
      // The exchange authenticated nobody: the factor is outside the
      // organization's allowed methods, or a second factor is owed. Retrying
      // the same way cannot succeed, so do not invite it.
      case "primary_required":
        return "Your organization does not allow signing in this way. Try the other provider, or contact Heavybit."
      case "mfa_required":
        return "This account needs a second sign-in step that is not available here yet. Contact Heavybit."
      case "session_setup_failed":
        return "Sign-in did not finish. Please try again."
      case "authentication_failed":
      case "intermediate_session_not_found":
        return "The sign-in could not be verified. Please start again."
      default:
        return "Sign-in failed. Please try again."
    }
  }
  return "Sign-in failed. Please try again."
}

/**
 * Human copy for a consent attempt that did not start.
 *
 * NOT describeExchangeError. That one describes the token EXCHANGE — a
 * sign-in — and its fallback is "Sign-in failed. Please try again.", which on
 * this page is wrong twice:
 *
 *   1. Sign-in succeeded. The person is holding a live session; that is why
 *      the consent page could ask Stytch anything at all, and why the account
 *      line and the switch link render underneath the error. Telling somebody
 *      their sign-in failed while they are signed in sends them to re-do the
 *      one step that worked.
 *   2. "Please try again" is advice that cannot work for the failure actually
 *      observed. On 2026-09-17 a Network-org member was refused the
 *      first-party Auger CLI because the organization carries
 *      `first_party_connected_apps_allowed_type: NOT_ALLOWED`. That refusal is
 *      permanent for that account and that application, so the page invited an
 *      infinite retry and named no way out — while the way out, switching
 *      account, was on screen the whole time.
 *
 * Deliberately keyed on nothing. Stytch does not document an `error_type` for
 * the policy refusal and this code has never captured one, so inventing a
 * string to switch on would be inventing a contract. What IS true of every
 * failure here is that the authorization cannot proceed for THIS account right
 * now, and that is what the copy says. When a real error type is in hand, add
 * a case above the fallback rather than rewriting this.
 *
 * IdentityNotConfiguredError keeps its own message: local dev and smoke tests
 * hit it routinely and it means something entirely different.
 */
export function describeConsentError(error: unknown): string {
  if (error instanceof IdentityNotConfiguredError) return describeExchangeError(error)
  // Our own exchange errors carry reasons this page can name precisely; they
  // reach here when the session behind the consent turns out to be refusable.
  if (error instanceof IdentityExchangeError) return describeExchangeError(error)
  return IDENTITY_COPY.authorize.startFailed
}
